export interface FinancialConfirmation {
  method: string;
  path: string;
  payload: Record<string, unknown>;
  resolve: (authorization: string) => void;
  reject: (error: Error) => void;
}

export class FinancialConfirmationCancelled extends Error {
  readonly code = "FINANCIAL_CONFIRMATION_CANCELLED";
}

/**
 * Motif proposé pour un encaissement saisi à la main (20/10/2026) : la pièce réglée et l'élève
 * (« Encaissement — Commande CMD-… (Élève) ») ; '' sans libellé. Partagé par la confirmation de sécurité
 * et la fenêtre « Nouvel encaissement » (23/10/2026). Ce fichier n'importe pas `http` (qui l'importe).
 */
export function suggestedPaymentReason(payload: Record<string, unknown>): string {
  const { articleName, candidateName } = payload;
  if (typeof articleName !== "string" || !articleName.trim()) return "";
  const student =
    typeof candidateName === "string" && candidateName.trim()
      ? ` (${candidateName.trim()})`
      : "";
  return `Encaissement — ${articleName.trim()}${student}`;
}

/**
 * Opérations d'argent faites sans mot de passe ni code (28/10/2026, décision de l'auto-école, J20), comme côté serveur
 * (`confirmationExempt`, action-target.ts) : encaisser, annuler, rejeter, remettre en banque, rembourser, rattacher,
 * contre-passer, corriger, rembourser un paiement en ligne, émettre un avoir, exporter les encaissements ou l'état
 * financier, clôturer une période.
 */
export function confirmationExempt(method: string, path: string) {
  if (method === "GET")
    return path === "/v1/payments/export" || path === "/v1/financial-statement/export";
  return (
    method === "POST" &&
    (path === "/v1/payments" ||
      /^\/v1\/payments\/[^/]+\/(attach|cancel|correct|deposit|reject|refund)$/.test(path) ||
      /^\/v1\/payments\/[^/]+\/operations\/[^/]+\/reverse$/.test(path) ||
      /^\/v1\/connect\/invoices\/[^/]+\/refund$/.test(path) ||
      /^\/v1\/invoices\/[^/]+\/credit-note$/.test(path) ||
      path === "/v1/fiscal-periods/closures")
  );
}

export function needsFinancialConfirmation(method: string, path: string) {
  if (confirmationExempt(method, path)) return false;
  if (
    method === "GET" &&
    path.startsWith("/v1/fiscal-periods/migration-runs/") &&
    path.endsWith("/download")
  )
    return true;
  if (
    method === "POST" &&
    (path === "/v1/compliance/candidates/export" ||
      path.startsWith("/v1/compliance/privacy-requests"))
  )
    return true;
  if (
    (method === "PATCH" &&
      (/^\/v1\/schools\/(?:me\/(?:profile|access-settings)|[^/]+)$/.test(
        path,
      ) ||
        /^\/v1\/auth\/staff\/[^/]+\/access$/.test(path) ||
        /^\/v1\/instructor-accounts\/[^/]+$/.test(path))) ||
    (method === "POST" &&
      (path === "/v1/auth/invite" ||
        /^\/v1\/auth\/staff\/[^/]+\/departure$/.test(path))) ||
    (["POST", "DELETE"].includes(method) &&
      /^\/v1\/schools\/me\/profile\/(logo|cachet)$/.test(path)) ||
    (["POST", "PATCH", "DELETE"].includes(method) &&
      path.startsWith("/v1/catalog/"))
  )
    return true;
  if (
    method === "POST" &&
    (path.startsWith("/v1/fiscal-periods/") ||
      /^\/v1\/invoices\/[^/]+\/credit-note$/.test(path))
  )
    return true;
  if (
    method === "GET" &&
    /^\/v1\/fiscal-periods\/(archives|archive-preparations)\/[^/]+\/download$/.test(
      path,
    )
  )
    return true;
  return (
    (method === "POST" &&
      (path === "/v1/payments" ||
        /^\/v1\/payments\/[^/]+\/(attach|cancel|correct|deposit|reject|refund)$/.test(
          path,
        ) ||
        /^\/v1\/payments\/[^/]+\/operations\/[^/]+\/reverse$/.test(path) ||
        /^\/v1\/connect\/invoices\/[^/]+\/refund$/.test(path))) ||
    (method === "GET" &&
      (path === "/v1/payments/export" ||
        // F40 : l'export de l'état financier se confirme comme celui des encaissements.
        path === "/v1/financial-statement/export" ||
        path === "/v1/fiscal-governance/security-export")) ||
    (method === "PATCH" && path === "/v1/schools/me/access-settings")
  );
}

export function confirmFinancialAction(
  method: string,
  path: string,
  payload: Record<string, unknown>,
): Promise<string> {
  return new Promise((resolve, reject) => {
    window.dispatchEvent(
      new CustomEvent<FinancialConfirmation>("permio:financial-confirmation", {
        detail: { method, path, payload, resolve, reject },
      }),
    );
  });
}
